/**
 * №53 · 53.4 — HANDLED, THE READING MODEL. Direction B, as ruled (E-31).
 *
 * Pure. No React, no fetch, no chrome. The page's decisions live here so their
 * EDGES can be asserted — an empty day, a band of one, a band where nothing
 * folds, a band where everything does — and none of those are what a fixture
 * drawn from the approved mockup would exercise. That is the standing trap this
 * repo has now hit three times: a fixture built from a drawing exercises exactly
 * what the drawing shows, which is precisely where the edges are not.
 *
 * ── WHAT A RECEIPT CAN AND CANNOT SAY (E-32, second finding) ──────────────────
 *
 * The approved drawing gives every line a right-hand fact — `−$288`, `0.4h`,
 * `SharePoint`, `Outlook`, `MatterX` — and gives every band an hours total. **A
 * receipt carries none of those.** `run_receipts` is
 * `(run_id, seq, task_id, verb, ok, kind, summary, ref, at)`: no money, no
 * duration, no destination system. The money and the hours in the drawing are
 * what a *time entry* and a *ledger posting* know, and neither is joined to a
 * receipt — `ref` is free text, not a foreign key, which is the same wall that
 * stopped hours-per-hire in 53.2.
 *
 * So the right-hand column here is **what it touched** (`ref`) where a receipt has
 * one, and is ABSENT otherwise; the band meta is a **count**, not an hours total.
 * A column that renders "0.4h" from nothing would be the most believable lie on
 * the page — it looks exactly like the drawing, and it is invented.
 *
 * The mechanism to do better exists and is one join away the day a receipt can
 * name a posting or an entry; until then the page says less and means it.
 *
 * ── THE FOLD (direction B) ───────────────────────────────────────────────────
 *
 * David chose B: inside a band, what wanted something from you renders at full
 * size and the repetitive bulk folds to one counted line. The SPLIT is the
 * server's (`isConsequential`, derived from the money / outbound / undo walls) —
 * this file only renders the fold's summary, and does it from the folded lines
 * themselves rather than from a hand-written phrase, so a fold can never describe
 * work it does not contain.
 */

export type UndoClass = "reversible" | "irreversible" | "closed";
export type UndoReason = "within_window" | "left_the_firm" | "window_expired" | "no_reversal";

export interface HandledUndo {
  klass: UndoClass;
  ms_left: number;
  reversal_verb: string | null;
  reason: UndoReason;
}

export interface HandledLine {
  id: string;
  run_id: string;
  seq: number;
  task_id: string | null;
  at: string;
  verb: string;
  ok: boolean;
  kind: string;
  summary: string | null;
  ref: string | null;
  actor_id: string | null;
  actor_name: string | null;
  undo: HandledUndo;
  consequential: boolean;
}

export interface HandledBand {
  key: string;
  title: string;
  span: string;
  source: string;
  axis: "clock" | "time_entry" | "calendar";
  lines: HandledLine[];
}

export interface HandledOwnTime {
  id: string;
  matter: string | null;
  narrative: string | null;
  units: number | null;
  billable: boolean;
}

export interface HandledDay {
  date: string;
  bands: HandledBand[];
  own_time: HandledOwnTime[];
  totals: { lines: number; consequential: number; folded: number; refused: number; hires: number };
  axis_note: string;
}

/** The clock, from a receipt's `YYYY-MM-DD HH:MM:SS`. Never re-parsed as a Date —
 *  receipts carry no zone, and `new Date()` on a zoneless string shifts the day. */
export function clockOf(at: string): string {
  const m = /(\d{2}):(\d{2})/.exec(at.slice(10));
  return m ? `${m[1]}:${m[2]}` : "--:--";
}

/**
 * The affordance one line is allowed to draw, and the words for it (D-CR15).
 * Returns the label the surface renders and whether it is actionable — the
 * surface never decides this for itself, because "offer an undo" is a promise
 * and the promise is the server's to make.
 */
export interface HandledAffordance {
  label: string;
  /** Only `reversible` is a button. Everything else is a statement. */
  actionable: boolean;
  /** The second, dimmer line: why there is no button. */
  note: string | null;
}

export function affordanceFor(line: HandledLine): HandledAffordance {
  if (!line.ok) return { label: "nudge", actionable: true, note: "blocked" };
  switch (line.undo.klass) {
    case "reversible": {
      const mins = Math.max(1, Math.round(line.undo.ms_left / 60_000));
      return { label: `undo ${mins}m`, actionable: true, note: null };
    }
    case "irreversible":
      // D-CR15: never an undo button for something that left the firm. The honest
      // alternative is a correction, and it is offered as the action.
      return { label: "correct →", actionable: true, note: "already sent" };
    case "closed":
      return {
        label: "inspect",
        actionable: true,
        note: line.undo.reason === "window_expired" ? `window closed ${clockOf(line.at)}` : null,
      };
  }
}

/** One line's headline. The runner's own summary when it wrote one; the verb otherwise. */
export function headlineOf(line: HandledLine): string {
  const s = (line.summary ?? "").trim();
  if (s) return s;
  // A receipt with no summary still has to say something true. The verb, humanised
  // only by punctuation — never re-worded, because re-wording a verb is the surface
  // inventing a description of work it did not observe.
  return line.verb.replace(/_/g, " ");
}

export interface HandledFold {
  count: number;
  /** The distinct verb families inside, in first-seen order: "created, filed, recorded". */
  verbs: string[];
  /** True when at least one folded line still has an open undo window. */
  any_actionable: boolean;
}

/**
 * The fold's summary, built from the folded lines themselves. Deliberately not a
 * phrase chosen per band: a fold that said "nothing raised" while containing a
 * refusal would be the exact failure the fold is accused of enabling.
 */
export function foldOf(lines: readonly HandledLine[]): HandledFold | null {
  const folded = lines.filter((l) => !l.consequential);
  if (folded.length === 0) return null;
  const verbs: string[] = [];
  for (const l of folded) {
    const head = l.verb.split("_")[0] ?? l.verb;
    if (!verbs.includes(head)) verbs.push(head);
  }
  return {
    count: folded.length,
    verbs,
    any_actionable: folded.some((l) => l.undo.klass === "reversible" || !l.ok),
  };
}

export function loudOf(lines: readonly HandledLine[]): HandledLine[] {
  return lines.filter((l) => l.consequential);
}

/**
 * The band's right-hand meta. A COUNT, not an hours total — see the header: a
 * receipt has no duration, so an hours figure here would be invented.
 */
export function bandMeta(band: HandledBand): string {
  const n = band.lines.length;
  return n === 1 ? "1 thing" : `${n} things`;
}

/** The page's one-line lede. Says what is open and what is folded, and never rounds. */
export function ledeOf(day: HandledDay): string {
  const { lines, consequential, hires } = day.totals;
  if (lines === 0) return "Nothing has been handled for you yet today.";
  const t = lines === 1 ? "1 thing" : `${lines} things`;
  const h = hires === 1 ? "one hire" : `${hires} hires`;
  const open = consequential === 0
    ? "None of it wanted anything from you — it is all folded, counted and one click away."
    : `${consequential === 1 ? "One of them" : `${consequential} of them`} wanted something from you or could still be undone — those are open below; the rest are folded, counted and one click away.`;
  return `${t} done for you today, across ${h}. ${open}`;
}
